import { useCallback } from 'react'

import { handleTabsKeyDown } from './utils/handleTabsKeyDown'

type TabsTheme = 'light' | 'dark'

type TabsItem = {
  key: string
  label: string
}

type TabsProps = {
  items: TabsItem[]
  activeIndex: number
  theme: TabsTheme
  ariaLabel?: string
  onChange: (index: number) => void
}

const isDarkTheme = (theme: TabsTheme) => theme === 'dark'

export default function Tabs({
  items,
  activeIndex,
  theme,
  ariaLabel = 'Tabs',
  onChange,
}: TabsProps) {
  const selectTab = useCallback(
    (nextTabIndex: number) => {
      if (nextTabIndex < 0 || nextTabIndex >= items.length) {
        return
      }

      onChange(nextTabIndex)
    },
    [items.length, onChange]
  )

  const onTabKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      handleTabsKeyDown({
        event,
        currentIndex: activeIndex,
        tabsLength: items.length,
        onSelectTab: selectTab,
      })
    },
    [activeIndex, items.length, selectTab]
  )

  if (items.length <= 1) {
    return null
  }

  return (
    <div
      className="mt-4 flex flex-wrap gap-2 md:mt-5"
      role="tablist"
      aria-label={ariaLabel}
    >
      {items.map(({ key, label }, index) => {
        const isActive = activeIndex === index

        return (
          <button
            key={key}
            id={`vitrine-large-tab-${key}`}
            role="tab"
            type="button"
            aria-selected={isActive}
            aria-controls={`vitrine-large-panel-${key}`}
            tabIndex={isActive ? 0 : -1}
            className={`rounded-full border px-4 py-2 text-xs font-semibold leading-none transition-colors md:text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-restructure-focus-primary ${
              isDarkTheme(theme)
                ? isActive
                  ? 'bg-restructure-background-primary text-restructure-primary border-restructure-border-primary'
                  : 'bg-restructure-background-tertiary-inverted text-restructure-primary-inverted border-restructure-border-secondary-inverted hover:border-restructure-border-hover-inverted'
                : isActive
                ? 'bg-restructure-background-neutral-12 text-restructure-primary-inverted border-restructure-background-neutral-12'
                : 'bg-white text-restructure-primary border-restructure-primary/20 hover:border-restructure-primary/35'
            }`}
            onClick={() => selectTab(index)}
            onKeyDown={onTabKeyDown}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
